import { AnimatePresence, animate, motion, useDragControls, useMotionValue } from "motion/react";
import { Fragment, type PropsWithChildren, useId, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Icon } from "../icons";
import { Portal } from "../portal";
import { cn } from "../utils";
import { FloatingControlContext } from "./context";

type Corner = "bottom-right" | "bottom-left" | "top-right" | "top-left";
type HiddenEdge = "left" | "right" | null;

const OFFSET = 16;
const SPRING = { type: "spring", stiffness: 400, damping: 35 } as const;

function cornerStyle(corner: Corner): React.CSSProperties {
	switch (corner) {
		case "bottom-right":
			return { bottom: OFFSET, right: OFFSET };
		case "bottom-left":
			return { bottom: OFFSET, left: OFFSET };
		case "top-right":
			return { top: OFFSET, right: OFFSET };
		case "top-left":
			return { top: OFFSET, left: OFFSET };
	}
}

/**
 * The app's one floating surface: a shell that can be dragged anywhere, snaps to the nearest
 * corner when let go, and slides off the left or right edge leaving only its handle behind.
 *
 * It carries no look of its own beyond the handle — whatever a `FloatingControlPanel` puts inside
 * brings its own surface. It was a dark pill of icon buttons once; that shape belonged to one
 * screen and made the shell unusable for anything that is not an icon.
 *
 * Rendered for every signed-in page and hidden by CSS while it holds nothing, so mounting it costs
 * nothing until something asks for it.
 */
export function FloatingControlProvider({ children }: PropsWithChildren) {
	/** Hooks */
	const chipRef = useRef<HTMLDivElement>(null);
	const handleRef = useRef<HTMLDivElement>(null);
	const dragControls = useDragControls();
	const renderId = useId();

	/** State */
	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const [corner, setCorner] = useState<Corner>("bottom-right");
	const [hiddenEdge, setHiddenEdge] = useState<HiddenEdge>(null);
	const x = useMotionValue(0);
	const y = useMotionValue(0);

	/** Functions */
	function handleDragEnd() {
		if (!chipRef.current || !handleRef.current) return;

		const rect = chipRef.current.getBoundingClientRect();
		const chipWidth = rect.width;
		const chipHeight = rect.height;
		const handleWidth = handleRef.current.getBoundingClientRect().width;
		const centerX = rect.left + chipWidth / 2;
		const centerY = rect.top + chipHeight / 2;
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		function naturalLeft(c: Corner) {
			return c.includes("right") ? viewportWidth - chipWidth - OFFSET : OFFSET;
		}
		function naturalTop(c: Corner) {
			return c.includes("top") ? OFFSET : viewportHeight - chipHeight - OFFSET;
		}

		const natLeft = naturalLeft(corner);
		const natTop = naturalTop(corner);

		if (centerX > viewportWidth) {
			setHiddenEdge("right");
			animate(x, viewportWidth - OFFSET - handleWidth - natLeft, SPRING);
		} else if (centerX < 0) {
			flushSync(() => setHiddenEdge("left"));
			animate(x, OFFSET + handleWidth - natLeft - chipWidth, SPRING);
		} else {
			const newCorner: Corner =
				centerY < viewportHeight / 2
					? centerX < viewportWidth / 2
						? "top-left"
						: "top-right"
					: centerX < viewportWidth / 2
						? "bottom-left"
						: "bottom-right";

			const targetX = naturalLeft(newCorner) - natLeft;
			const targetY = naturalTop(newCorner) - natTop;

			Promise.all([animate(x, targetX, SPRING), animate(y, targetY, SPRING)]).then(() => {
				flushSync(() => {
					setCorner(newCorner);
					setHiddenEdge(null);
				});
				x.set(0);
				y.set(0);
			});
		}
	}

	/** Render */
	return (
		<Fragment>
			<FloatingControlContext.Provider value={{ container }}>
				{children}
			</FloatingControlContext.Provider>
			<Portal>
				<motion.div
					ref={chipRef}
					layout="size"
					layoutId={renderId}
					style={{ ...cornerStyle(corner), x, y }}
					// Fixed rather than absolute: portalled onto the body, `absolute` anchors to the
					// document and drifts with it. And above the modal layer at z-500, so something
					// started from a dialog stays reachable while the dialog is open.
					className={cn(
						"floating-control-provider",
						"fixed z-510",
						"h-stack items-center gap-1",
						hiddenEdge === "left" && "flex-row-reverse",
					)}
					drag
					dragListener={false}
					dragControls={dragControls}
					dragMomentum={false}
					onDragEnd={handleDragEnd}
				>
					<AnimatePresence mode="wait">
						<motion.div
							key={hiddenEdge ? "expanded" : "drag-handle"}
							ref={handleRef}
							initial={{
								scale: 0,
							}}
							animate={{
								scale: 1,
							}}
							className={cn(
								"card flex-center shadow-lg",
								"px-0.5 py-3 text-mauve-11",
								"cursor-grab active:cursor-grabbing",
								hiddenEdge === "right" && "-translate-x-1",
							)}
							onPointerDown={(event) => {
								dragControls.start(event);
							}}
						>
							<Icon
								name={
									hiddenEdge === "right"
										? "ChevronLeft"
										: hiddenEdge === "left"
											? "ChevronRight"
											: "GripVertical"
								}
							/>
						</motion.div>
					</AnimatePresence>
					<div
						ref={setContainer}
						className="h-stack items-center floating-control-container gap-2"
					/>
				</motion.div>
			</Portal>
		</Fragment>
	);
}
